import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { exportProgress, loadProgress, resetProgress, saveProgress } from "../engine";
import type { Challenge, ChapterFilter, GateId, NextMode } from "../engine";
import { initGame, reducer, type GameState, type ViewId } from "./duelStore";

import { createLearningPublisher, type SyncState } from "../lib/learningSync";
import type { LearningUnitCompletion } from "../lib/learningEvidence";
import { loadPreferences, savePreferences, type Preferences } from "../lib/preferences";

interface GameActions {
  setView: (view: ViewId) => void;
  start: (opts?: { mode?: NextMode; chapter?: ChapterFilter; id?: number }) => void;
  play: (gate: GateId) => void;
  hint: () => void;
  answer: (optionId: string) => void;
  close: () => void;
  continueChallenge: (challenge: Challenge) => void;
  continueVictory: (quoteId: number) => void;
  endTurn: () => void;
  restart: () => void;
  next: (mode?: NextMode) => void;
  jumpChapter: (chapter: ChapterFilter) => void;
  setMode: (mode: NextMode) => void;
  toggleLearn: () => void;
  clearFx: () => void;
  exportSave: () => void;
  reset: () => void;
}

interface GameContextValue {
  state: GameState;
  actions: GameActions;
  sync: SyncState;
  localSaved: boolean;
  submitCompletion: (completion: LearningUnitCompletion) => void;
  retrySync: () => void;
  preferences: Preferences;
  setPreferences: (value: Preferences) => void;
}

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, () => initGame(loadProgress()));

  const [sync, setSync] = useState<SyncState>({ phase: "idle", pending: 0, retryable: false });
  const [localSaved, setLocalSaved] = useState(false);
  const publisherRef = useRef<ReturnType<typeof createLearningPublisher> | null>(null);
  useEffect(() => {
    const publisher = createLearningPublisher(setSync);
    publisherRef.current = publisher;
    void publisher.start();
    const resume = () => { void publisher.retry(); };
    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    return () => {
      publisher.stop();
      window.removeEventListener("online", resume);
      window.removeEventListener("focus", resume);
      if (publisherRef.current === publisher) publisherRef.current = null;
    };
  }, []);
  const submitCompletion = (completion: LearningUnitCompletion) => { void publisherRef.current?.submit(completion); };
  const retrySync = () => { void publisherRef.current?.retry(); };
  const [preferences, setPreferences] = useState(loadPreferences);
  const progressRef = useRef(state.progress);
  progressRef.current = state.progress;
  useEffect(() => savePreferences(preferences), [preferences]);

  useEffect(() => {
    setLocalSaved(saveProgress(state.progress));
  }, [state.progress]);

  const actions = useMemo<GameActions>(
    () => ({
      setView: (view) => dispatch({ type: "SET_VIEW", view }),
      start: (opts) => dispatch({ type: "START", ...opts }),
      play: (gate) => dispatch({ type: "PLAY_GATE", gate }),
      hint: () => dispatch({ type: "HINT" }),
      answer: (optionId) => dispatch({ type: "ANSWER", optionId }),
      close: () => dispatch({ type: "CLOSE_CHALLENGE" }),
      continueChallenge: (challenge) => dispatch({ type: "CONTINUE_CHALLENGE", challenge }),
      continueVictory: (quoteId) => dispatch({ type: "CONTINUE_VICTORY", quoteId }),
      endTurn: () => dispatch({ type: "END_TURN" }),
      restart: () => dispatch({ type: "RESTART" }),
      next: (mode) => dispatch({ type: "NEXT", mode }),
      jumpChapter: (chapter) => dispatch({ type: "JUMP_CHAPTER", chapter }),
      setMode: (mode) => dispatch({ type: "SET_MODE", mode }),
      toggleLearn: () => dispatch({ type: "TOGGLE_LEARN" }),
      clearFx: () => dispatch({ type: "CLEAR_FX" }),
      exportSave: () => exportProgress(progressRef.current),
      reset: () => dispatch({ type: "HYDRATE", progress: resetProgress() }),
    }),
    [],
  );

  const value = { state, actions, preferences, setPreferences, sync, localSaved, submitCompletion, retrySync };
  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
}
